// Shared helpers + components for the mobile tech app (/tech routes).
// Additive module — nothing here is imported by the desktop pages.
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { inventoryApi, partsApi } from '@/services/api';

// Same palette the desktop TicketsPage uses — keep the visual language.
export const techStatusColors: Record<string, { bg: string; color: string }> = {
  new: { bg: '#e0e7ff', color: '#4f46e5' },
  unassigned: { bg: '#e0e7ff', color: '#4f46e5' },
  assigned: { bg: '#dbeafe', color: '#2563eb' },
  scheduled: { bg: '#dbeafe', color: '#2563eb' },
  en_route: { bg: '#fef3c7', color: '#d97706' },
  on_site: { bg: '#d1fae5', color: '#059669' },
  in_progress: { bg: '#cffafe', color: '#0891b2' },
  pending_parts: { bg: '#fce7f3', color: '#db2777' },
  awaiting_parts: { bg: '#ede9fe', color: '#6d28d9' },
  completed: { bg: '#dcfce7', color: '#16a34a' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280' },
};

export const techPriorityColors: Record<string, { bg: string; color: string }> = {
  urgent: { bg: '#fee2e2', color: '#dc2626' },
  high: { bg: '#ffedd5', color: '#ea580c' },
  medium: { bg: '#fef3c7', color: '#d97706' },
  low: { bg: '#dbeafe', color: '#2563eb' },
};

export const CONCEPT_GRADIENT = 'linear-gradient(180deg, #FF4A4A, #E30613 55%, #B00010)';   // design standard .btn-accent (2026-09-04)

// /auth/me returns roles as OBJECTS {id, name} — tolerate strings too.
export const isTechnician = (user: any): boolean =>
  ((user?.roles || []) as any[]).some(
    (r) => (typeof r === 'string' ? r : r?.name) === 'Technician',
  );

export const ticketAddress = (t: any): string =>
  [t?.service_address_line1, t?.service_city, t?.service_state, t?.service_postal_code]
    .filter(Boolean)
    .join(', ');

export const mapsHref = (t: any): string => {
  const q = ticketAddress(t) || t?.customer_name || t?.title || '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

// Naive local ISO (no timezone) — matches how the desktop manual-entry
// datetime-local fields feed the time-tracking API.
export const toLocalIso = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export const fmtMinutes = (m: number): string =>
  m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : `${Math.round(m)}m`;

export const fmtSched = (iso?: string | null): string => {
  if (!iso) return 'Not scheduled';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Not scheduled';
  return d.toLocaleString([], {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

export function StatusChip({ status, size = 'small' }: { status: string; size?: 'small' | 'medium' }) {
  const c = techStatusColors[status] || techStatusColors.new;
  return (
    <Chip
      label={(status || '').replace(/_/g, ' ').toUpperCase()}
      size={size}
      sx={{ fontWeight: 700, bgcolor: c.bg, color: c.color }}
    />
  );
}

export function ReturnTripChip({ ticket }: { ticket: any }) {
  const isReturnVisit = !!ticket?.return_of_ticket_id;
  const needsReturn = !!(ticket?.return_trip_required || ticket?.needs_return);
  if (!isReturnVisit && !needsReturn) return null;
  return (
    <Chip
      label={isReturnVisit ? 'RETURN TRIP' : '⚠ NEEDS RETURN'}
      size="small"
      sx={{
        fontWeight: 700,
        bgcolor: isReturnVisit ? '#ede9fe' : '#fff7ed',
        color: isReturnVisit ? '#6d28d9' : '#c2410c',
        border: isReturnVisit ? 'none' : '1px solid #fdba74',
      }}
    />
  );
}

// ── Transfer quick-action: request a part to my truck ──────────────────────
interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
  initialPart?: any | null;
}

export function TransferRequestDialog({ open, onClose, onDone, initialPart }: TransferDialogProps) {
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [part, setPart] = useState<any | null>(null);
  const [stock, setStock] = useState<any[]>([]);
  const [qty, setQty] = useState<number>(1);
  const [from, setFrom] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const myTruck = warehouses.find((w) => w.is_mine)?.warehouse as string | undefined;

  useEffect(() => {
    if (!open) return;
    setPart(initialPart || null);
    setQuery(''); setResults([]); setQty(1); setNote(''); setError(null); setOk(false);
    inventoryApi.warehouses()
      .then((r) => {
        setWarehouses(r.data);
        const main = (r.data || []).find((w: any) => w.is_main)?.warehouse;
        setFrom(main || 'Stores - CC');
      })
      .catch(() => setWarehouses([]));
  }, [open, initialPart]);

  useEffect(() => {
    if (part?.part_number) {
      inventoryApi.partStock(part.part_number).then((r) => setStock(r.data)).catch(() => setStock([]));
    } else {
      setStock([]);
    }
  }, [part]);

  const search = useCallback(async (q: string) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try { const r = await partsApi.search(q); setResults(r.data || []); }
    catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const submit = async () => {
    if (!part?.part_number || qty <= 0) return;
    setBusy(true); setError(null);
    try {
      await inventoryApi.createTransfer({
        part_number: part.part_number,
        qty: Number(qty),
        from_warehouse: from,
        to_warehouse: myTruck || undefined, // backend defaults to requester's truck
        note: note || undefined,
      });
      setOk(true);
      onDone && onDone();
      setTimeout(onClose, 900);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to request transfer');
    } finally {
      setBusy(false);
    }
  };

  const whShort = (w: string) => (w || '').replace(/ - CC$/, '').replace(/^Truck - /, '🚚 ');

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Request Part → My Truck</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}>
        {ok && <Alert severity="success">Transfer requested — the source location will accept, deny, or order it.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {!part ? (
          <>
            <TextField
              autoFocus size="medium" label="Search parts catalog"
              placeholder="Name or part number…" value={query}
              onChange={(e) => search(e.target.value)}
              InputProps={{ endAdornment: searching ? <CircularProgress size={18} /> : null }}
            />
            {results.length > 0 && (
              <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
                {results.map((p: any) => (
                  <Box
                    key={p.id}
                    onClick={() => setPart(p)}
                    sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', '&:active': { bgcolor: '#f1f5f9' } }}
                  >
                    <Typography variant="body2" fontWeight={700}>{p.part_number}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.name}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{part.part_number}</Typography>
                  <Typography variant="caption" color="text.secondary">{part.name}</Typography>
                </Box>
                <Button size="small" onClick={() => setPart(null)}>Change</Button>
              </Box>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {stock.length === 0
                  ? <Typography variant="caption" color="text.secondary">No stock records in ERP</Typography>
                  : stock.map((s: any) => (
                    <Chip key={s.warehouse} size="small" variant="outlined"
                      color={s.qty < 0 ? 'error' : s.warehouse === myTruck ? 'primary' : 'default'}
                      label={`${whShort(s.warehouse)}: ${s.qty}`} />
                  ))}
              </Box>
            </Box>
            <TextField
              size="medium" type="number" label="Quantity" value={qty}
              inputProps={{ min: 1, inputMode: 'numeric' }}
              onChange={(e) => setQty(Number(e.target.value))}
            />
            <FormControl fullWidth>
              <InputLabel>From (source location)</InputLabel>
              <Select value={from} label="From (source location)" onChange={(e) => setFrom(e.target.value)}>
                {warehouses.map((w: any) => (
                  <MenuItem key={w.warehouse} value={w.warehouse}>
                    {whShort(w.warehouse)}{w.is_main ? ' (main warehouse)' : ''}{w.is_mine ? ' (your truck)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="medium" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <Alert severity="info" sx={{ py: 0 }}>
              Delivered to <b>{myTruck ? whShort(myTruck) : 'your truck'}</b> once the source accepts (stock moves in ERP).
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ minHeight: 44 }}>Cancel</Button>
        <Button
          variant="contained" onClick={submit}
          disabled={busy || !part || !from || qty <= 0}
          sx={{ minHeight: 44, background: CONCEPT_GRADIENT }}
        >
          {busy ? 'Requesting…' : 'Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
